import { FC, useMemo } from 'react';

import { ColumnTable, Entry } from '@/components/ColumnTable';
import { RelationName } from '@/components/RelationName';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useAllowDbtCloudLinks } from '@/context/DbtCloudProvider';
import { usePublicModels } from '@/hooks/discovery/publicModels';
import { useLinkToExplorer } from '@/hooks/links';
import { ProjectScope } from '@/hooks/params';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { truthy } from '@/util/array';
import { PanelDetails, ResourceDetailsParams } from './panelDetailTypes';

export const usePublicModelDetailsPanel = ({
  uniqueId,
  accountId,
  projectId,
  environmentId,
  projectName,
}: ResourceDetailsParams) => {
  const { projectId: originProjectId } = useDbtCloudProject();
  const publicModelsQuery = usePublicModels({
    first: 1,
    accountId,
    environmentIds: [environmentId!],
    uniqueIds: [uniqueId.raw],
    pause: !uniqueId.raw,
  });

  const getTargetEnvironmentType = useTargetEnvironmentType();

  const publicModel = useMemo(
    () =>
      publicModelsQuery.data?.find((node) => {
        return node.uniqueId === uniqueId.raw;
      }),
    [publicModelsQuery, uniqueId.raw],
  );

  const getResourceLink = useLinkToExplorer('resourceDetails');
  const getLineageLink = useLinkToExplorer('projectLineage');
  const allowExternalLinks = useAllowDbtCloudLinks();

  return useMemo(() => {
    const { error, fetching } = publicModelsQuery;
    if (!publicModel) {
      return {
        ...publicModelsQuery,
        error,
        fetching,
        rows: null,
        description: null,
      } satisfies PanelDetails;
    }

    const fqn = publicModel?.fqn?.filter(truthy).join('.');

    const projectScope: ProjectScope = {
      accountId: accountId || 0,
      projectId: projectId || 0,
      environmentType: getTargetEnvironmentType(publicModel.environmentId),
    };

    const rows: Entry[] = [
      // Need to indicate this is an external project
      {
        key: 'Name',
        data: uniqueId.remainder,
        link: getResourceLink({
          ...projectScope,
          dbtUniqueId: uniqueId.raw,
          originProject: originProjectId,
        }),
        // if snowflake crossProjectLink false
        crossProjectLink: allowExternalLinks,
      },
      {
        key: 'Project',
        data: projectName || projectId,
        link: getLineageLink({
          ...projectScope,
          selector: fqn ? `1+${fqn}+1` : undefined,
          originProject: originProjectId,
        }),
        crossProjectLink: allowExternalLinks,
      },
      {
        key: 'Relation',
        data: <RelationName relation={publicModel} />,
        disableTooltip: true,
      },

      {
        key: 'Package',
        data: uniqueId.packageName,
      },
    ];

    return {
      error,
      fetching,
      rows,
      description: publicModel.description,
      deprecationDate: publicModel.deprecationDate,
    } satisfies PanelDetails & { deprecationDate: string | null };
  }, [
    publicModelsQuery,
    publicModel,
    accountId,
    projectId,
    getTargetEnvironmentType,
    uniqueId.raw,
    uniqueId.remainder,
    uniqueId.packageName,
    getResourceLink,
    originProjectId,
    allowExternalLinks,
    projectName,
    getLineageLink,
  ]);
};

export const PublicModelDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  ...props
}) => {
  const publicModelDetails = usePublicModelDetailsPanel(props);
  return (
    <div className={className}>
      {children?.(publicModelDetails)}
      <ColumnTable
        compact={compact}
        tableEntries={publicModelDetails.rows}
        isLoading={publicModelDetails.fetching}
      />
    </div>
  );
};
